<!-- eslint-disable vue/no-mutating-props -->
<template>
  <div class="mrp-tab" :class="{ 'form-disabled': disabled }">
    <div class="card">
      <div class="card-header py-3 pb-2" style="padding-left: 1rem">
        <h6 class="mb-0 fw-semibold">MRP</h6>
        <!-- Baris MRP = baris tabel Plant (FORM02 `rows_from`): tambah/hapus baris dari tab Plant & Storage Location. -->
        <small class="text-muted">Rows follow the Plant table in the Plant &amp; Storage Location tab.</small>
      </div>
      <div class="card-body pt-0 px-0 card-body-tab">
        <DataTable :value="dialog.form[MRPS_FIELD] || []" class="p-datatable-sm p-datatable-gridlines" scrollable scroll-height="420px">
          <template v-for="col in MRP_COLUMNS" :key="col.column">
            <Column v-if="isColumnVisibleFn(col.column, MRPS_FIELD, MRP_TAB)" header-class="bg-light text-center" :style="columnWidth(col.width)">
              <template #header>
                {{ col.label }} <span v-if="isColumnMandatoryInRows(MRP_TAB, MRPS_FIELD, col.column)" class="exclamation-mark-error-tab">*</span>
              </template>
              <template #body="{ data }">
                <!-- Select: opsi dari data_source config, di-cache per param request (mis. MRP Controller & Sloc for EP per plant baris). -->
                <SelectPrime show-clear v-if="col.type === 'select'" v-model="data[col.column]" size="small" fluid filter
                  :options="dialog.options[columnOptionsKey(MRP_TAB, MRPS_FIELD, col.column, data)]"
                  option-label="label" option-value="value" :placeholder="`Select ${col.label}`"
                  :loading="dialog.loading[columnOptionsKey(MRP_TAB, MRPS_FIELD, col.column, data)]"
                  :invalid="isColumnInvalidFn(data, col.column)"
                  :disabled="disabled || col.readonly || !isColumnEnabledFn(col.column, MRPS_FIELD, MRP_TAB) || isColumnLocked(MRP_TAB, MRPS_FIELD, col.column, data) || !isColumnDependencyReadyFn(col.column, MRPS_FIELD, MRP_TAB, data)"
                  @show="loadColumnOptions(MRP_TAB, MRPS_FIELD, col.column, data)"
                  @change="onColumnChange(MRP_TAB, MRPS_FIELD, col.column, data)"
                >
                  <template #value="{ value, placeholder }">
                    <span v-if="value != null && value !== ''">{{ getSelectValueLabelFn(dialog.options[columnOptionsKey(MRP_TAB, MRPS_FIELD, col.column, data)], value) }}</span>
                    <span v-else>{{ placeholder }}</span>
                  </template>
                </SelectPrime>
                <InputNumber v-else-if="col.type === 'number'" :model-value="toNumberValue(data[col.column])" size="small" placeholder="0" :use-grouping="false" fluid
                  :invalid="isColumnInvalidFn(data, col.column)"
                  :disabled="disabled || !isColumnEnabledFn(col.column, MRPS_FIELD, MRP_TAB) || isColumnLocked(MRP_TAB, MRPS_FIELD, col.column, data)"
                  @update:model-value="data[col.column] = $event"
                />
                <div v-else-if="col.type === 'checkbox'" class="text-center">
                  <Checkbox v-model="data[col.column]" :binary="true" true-value="X" :false-value="null"
                    :disabled="disabled || !isColumnEnabledFn(col.column, MRPS_FIELD, MRP_TAB) || isColumnLocked(MRP_TAB, MRPS_FIELD, col.column, data)"
                  />
                </div>
                <InputText v-else v-model="data[col.column]" size="small" :placeholder="col.readonly ? '' : col.label" fluid
                  :invalid="isColumnInvalidFn(data, col.column)"
                  :disabled="disabled || col.readonly || !isColumnEnabledFn(col.column, MRPS_FIELD, MRP_TAB) || isColumnLocked(MRP_TAB, MRPS_FIELD, col.column, data)"
                />
              </template>
            </Column>
          </template>
          <template #empty>
            <div class="text-muted text-center small py-3">No plant yet. Add a plant in the Plant &amp; Storage Location tab.</div>
          </template>
        </DataTable>
      </div>
    </div>
  </div>
</template>

<script>
import { cataloguerTabMixin, useCataloguerTabEngine, createTabDialog } from "./New_CataloguerTab";

// Key tab & field repeatable di FORM02_BASE.json
const MRP_TAB = "mrp";
const MRPS_FIELD = "cat_emcr_mrps";

// Layout kolom (urutan, label, jenis input) -- hardcode di Vue sesuai prinsip "layout hardcode, behavior dynamic".
// Tampil/wajib/enabled/opsi tiap kolom tetap dari config FORM02 (cat_emcr_mrps.columns). Kolom baru = tambah 1 baris
// di sini + config-nya di JSON. `readonly` (tidak diedit di sini): Plant = penanda baris (diisi dari tabel Plant),
// Service Level = turunan MRP Controller yang dipilih (default_value `derived`, diisi onColumnChange). Co Product: "X" / null.
// Tanda * dihitung per baris (isColumnMandatoryInRows): sebagian aturan wajib MRP bergantung pada plant / MRP Controller baris.
const MRP_COLUMNS = [
  { column: "plant", label: "Plant", type: "select", width: 160, readonly: true },
  { column: "mrp_profile", label: "MRP Profile", type: "text", width: 160 },
  { column: "sparepart_category", label: "Item Category", type: "select", width: 210 },
  { column: "mrp_type", label: "MRP Type", type: "select", width: 180 },
  { column: "lot_size", label: "Lot Size", type: "select", width: 160 },
  { column: "mrp_group", label: "MRP Group", type: "select", width: 180 },
  { column: "safety_stock", label: "Safety Stock", type: "number", width: 140 },
  { column: "reorder_point", label: "Reorder Point", type: "number", width: 140 },
  { column: "max_stock", label: "Maximum Stock Level", type: "number", width: 170 },
  { column: "min_lotsize", label: "Min Lot Size", type: "number", width: 140 },
  { column: "max_lotsize", label: "Max Lot Size", type: "number", width: 140 },
  { column: "fixed_lot", label: "Fixed Lot Size", type: "number", width: 140 },
  { column: "procurement_type", label: "Procurement Type", type: "select", width: 190 },
  { column: "special_procurement_type", label: "Special Procurement Type", type: "select", width: 220 },
  { column: "material_freight_group", label: "Material Freight Group", type: "select", width: 210 },
  { column: "gr_processing_time", label: "GR Processing Time", type: "number", width: 170 },
  { column: "planned_delivery", label: "Planned Delivery Time", type: "number", width: 180 },
  { column: "mrp_controller", label: "MRP Controller", type: "select", width: 190 },
  { column: "service_level", label: "Service Level", type: "text", width: 150, readonly: true },
  { column: "abc_indicator", label: "ABC Indicator", type: "select", width: 170 },
  { column: "avail_check", label: "Avail Check", type: "select", width: 170 },
  { column: "sloc_for_ep", label: "Sloc for EP", type: "select", width: 180 },
  { column: "rounding_value", label: "Rounding Value", type: "number", width: 150 },
  { column: "co_product", label: "Co Product", type: "checkbox", width: 120 },
  { column: "sm_key", label: "Sched Margin Key", type: "select", width: 180 },
  { column: "requirement_group", label: "Requirement Group", type: "select", width: 190 },
  { column: "individual_collective", label: "Individual or Collective", type: "select", width: 200 }
];

// Props, setup engine, dialog, bind ke mcrItem, opsi kolom & label: kerangka bersama New_CataloguerTab.js.
export default {
  name: "MrpTab",
  mixins: [cataloguerTabMixin],
  setup(props) {
    return useCataloguerTabEngine(props);
  },
  data() {
    return {
      MRP_TAB,
      MRPS_FIELD,
      MRP_COLUMNS,
      dialog: createTabDialog(this.mcrItem)
    };
  },
  methods: {
    columnWidth(width) {
      return { width: `${width}px`, minWidth: `${width}px` };
    },
    // Hook kerangka bersama, dipanggil tiap item dipilih/berganti. cat_emcr_mrps sudah di-link ke cat_emcr_plants oleh
    // New_CataloguerForm.hydrateItem (rows_from), jadi tidak perlu ensureTableArrays di sini.
    onItemBound() {
      // Service Level kosong (data lama) diisi dari MRP Controller baris itu -- sebelum preload label, lihat kerangka.
      this.fillEmptyColumnDefaults(MRP_TAB);
      this.preloadTabLabels(MRP_TAB);
      // Otomasi baris dari config (row_autofill / auto_select_single kolom), termasuk pemicu level form (Material
      // Type/Group di Basic Data). Mode lihat-saja dilewati di kerangka.
      this.watchExternalRowTriggers(MRP_TAB);
      this.applyRowAutomationOnOpen(MRP_TAB);
    }
  }
};
</script>
